// Nawigacja krokowa.
//
// Ekrany: 0 = start (liczony jako krok 1 z 9 — endowed progress), 1–8 = kroki,
// 9 = „Sprawdź odpowiedzi". Do tego lista sekcji „Twój brief" jako nakładka.
//
// Zasady:
// - „Dalej" przechodzi przez bramkę walidacji, „Wstecz" nigdy (nie więzimy),
// - Enter w polu = Dalej; w textarea Enter łamie wiersz, a Ctrl/⌘+Enter idzie dalej,
// - każdy krok ma swój wpis w historii (#krok-3) — przycisk Wstecz przeglądarki
//   MUSI cofać o krok, a nie wyrzucać ze strony w połowie wypełniania,
// - deep-link wpuszcza tylko na krok, do którego klient realnie doszedł.

import { announce, focusHeading, prefersReducedMotion } from "./a11y.js";
import { validateStep, stepIsComplete, stepHasContent } from "./validate.js";

export function initSteps(form, options = {}) {
    const intro = document.querySelector("[data-intro]");
    const review = form.querySelector("[data-review]");
    const tasklist = document.querySelector("[data-tasklist]");
    const steps = Array.from(form.querySelectorAll(".brief__step"));
    const total = steps.length;
    const REVIEW = total + 1;
    const displayTotal = total + 1; // ekran startowy liczy się jako krok

    const prevBtn = form.querySelector("[data-prev]");
    const nextBtn = form.querySelector("[data-next]");
    const submitBtn = form.querySelector("[data-submit]");
    const submitHint = form.querySelector("[data-submit-hint]");
    const navHint = form.querySelector("[data-nav-hint]");
    const progressNow = document.querySelector("[data-progress-now]");
    const progressFill = document.querySelector("[data-progress-fill]");
    const progressBar = document.querySelector("[data-progress-bar]");
    const railItems = Array.from(document.querySelectorAll("[data-progress-step]"));

    let current = 0;
    let tasklistOpen = false;
    let returnToReview = false; // „Zmień" z podsumowania wraca na podsumowanie

    function stepEl(nr) {
        return steps.find((el) => Number(el.dataset.step) === nr) || null;
    }

    function stepTitle(nr) {
        const el = stepEl(nr);
        const title = el ? el.querySelector(".brief__stepTitle") : null;
        return title ? title.textContent.trim() : "";
    }

    function screenLabel(nr) {
        if (nr === 0) return "Zanim zaczniemy";
        if (nr === REVIEW) return "Sprawdź odpowiedzi";
        return stepTitle(nr);
    }

    function hashFor(nr) {
        if (nr === 0) return "#start";
        if (nr === REVIEW) return "#podsumowanie";
        return "#krok-" + nr;
    }

    function nrFromHash(hash) {
        if (hash === "#start") return 0;
        if (hash === "#podsumowanie") return REVIEW;
        const match = /^#krok-(\d+)$/.exec(hash || "");
        if (!match) return null;
        const nr = Number(match[1]);
        return nr >= 1 && nr <= total ? nr : null;
    }

    // Pierwszy krok, którego nie da się uznać za ukończony — granica deep-linku.
    function firstIncomplete() {
        for (let nr = 1; nr <= total; nr++) {
            const el = stepEl(nr);
            if (el && !stepIsComplete(el, form)) return nr;
        }
        return total + 1;
    }

    function paintProgress() {
        const shown = current === REVIEW ? displayTotal : current + 1;
        const label = screenLabel(current);

        if (progressNow) {
            progressNow.innerHTML =
                current === REVIEW
                    ? "Ostatni krok <span>· " + label + "</span>"
                    : "Krok " + shown + " z " + displayTotal + " <span>· " + label + "</span>";
        }
        if (progressFill) progressFill.style.width = (shown / displayTotal) * 100 + "%";
        if (progressBar) progressBar.setAttribute("aria-valuenow", String(shown));

        railItems.forEach((item) => {
            const nr = Number(item.dataset.progressStep);
            const done = current === REVIEW || nr < current;
            item.dataset.state = done ? "done" : nr === current ? "now" : "next";
            if (nr === current && current !== REVIEW) {
                item.setAttribute("aria-current", "step");
            } else {
                item.removeAttribute("aria-current");
            }
            const bullet = item.querySelector(".brief__progressBullet");
            if (bullet) {
                bullet.innerHTML = done
                    ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>'
                    : String(nr);
            }
        });
    }

    function paint() {
        if (intro) intro.hidden = tasklistOpen || current !== 0;
        if (tasklist) tasklist.hidden = !tasklistOpen;
        form.hidden = tasklistOpen || current === 0;

        // animację wejścia robi CSS (.brief__step:not([hidden])) — JS tylko
        // przełącza widoczność, żeby nic nie mogło zostawić kroku w klatce startowej
        steps.forEach((el) => {
            el.hidden = tasklistOpen || Number(el.dataset.step) !== current;
        });
        if (review) review.hidden = tasklistOpen || current !== REVIEW;

        const onForm = !tasklistOpen && current >= 1;
        if (prevBtn) prevBtn.hidden = !onForm;
        if (nextBtn) nextBtn.hidden = !onForm || current === REVIEW;
        if (submitBtn) submitBtn.hidden = !onForm || current !== REVIEW;
        if (submitHint) submitHint.hidden = current !== REVIEW;
        if (navHint) navHint.hidden = current === REVIEW;
        if (nextBtn) {
            const label = nextBtn.querySelector("span");
            if (label) label.textContent = current === total ? "Sprawdź odpowiedzi" : "Dalej";
        }

        paintProgress();
    }

    function focusScreen() {
        if (tasklistOpen && tasklist) {
            focusHeading(tasklist.querySelector(".brief__tasklistTitle"));
            return;
        }
        if (current === 0 && intro) {
            focusHeading(intro.querySelector(".brief__introTitle"));
            return;
        }
        if (current === REVIEW && review) {
            focusHeading(review.querySelector(".brief__reviewTitle"));
            return;
        }
        const el = stepEl(current);
        focusHeading(el ? el.querySelector(".brief__stepTitle") : null);
    }

    function render(opts) {
        paint();
        if (!opts.silent) {
            announce(
                current === REVIEW
                    ? "Sprawdź odpowiedzi przed wysłaniem."
                    : "Krok " + (current + 1) + " z " + displayTotal + ": " + screenLabel(current)
            );
            focusScreen();
        }
        window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }

    function goTo(nr, opts = {}) {
        const target = Math.max(0, Math.min(REVIEW, nr));
        const changed = target !== current || tasklistOpen;
        current = target;
        tasklistOpen = false;

        // history bywa niedostępna (sandbox, file://, wbudowane przeglądarki
        // w aplikacjach) — brak wpisu w historii nie może wywrócić nawigacji
        try {
            if (changed && !opts.replace) {
                history.pushState({ briefStep: current }, "", hashFor(current));
            } else if (opts.replace) {
                history.replaceState({ briefStep: current }, "", hashFor(current));
            }
        } catch {
            /* zostaje nawigacja bez deep-linku */
        }

        // View Transitions: fallback jest darmowy — brak wsparcia = brak animacji
        if (document.startViewTransition && !prefersReducedMotion() && !opts.silent) {
            document.startViewTransition(() => render(opts));
        } else {
            render(opts);
        }

        if (options.onStepChange) options.onStepChange(current);
    }

    function openTaskList() {
        tasklistOpen = true;
        try {
            history.pushState({ briefStep: current, tasklist: true }, "", "#twoj-brief");
        } catch {
            /* jw. */
        }
        paintTaskList();
        render({});
        if (options.onStepChange) options.onStepChange(current);
    }

    function paintTaskList() {
        if (!tasklist) return;
        tasklist.querySelectorAll("[data-task-step]").forEach((btn) => {
            const nr = Number(btn.dataset.taskStep);
            const el = stepEl(nr);
            const status = btn.querySelector("[data-task-status]");
            if (!el || !status) return;
            // trzy statusy, zwykłym tekstem — HMRC po badaniach zrezygnował
            // z kolorowych plakietek, bo były trudne do odczytania
            const started = stepHasContent(el);
            if (started && stepIsComplete(el, form)) {
                status.textContent = "Ukończono";
                status.dataset.status = "done";
            } else if (started) {
                status.textContent = "W trakcie";
                status.dataset.status = "progress";
            } else {
                status.textContent = "Nie rozpoczęto";
                status.dataset.status = "none";
            }
        });
    }

    // ---- zdarzenia ----

    if (nextBtn) {
        nextBtn.addEventListener("click", () => {
            const el = stepEl(current);
            if (el && !validateStep(el, form)) return;
            if (returnToReview) {
                returnToReview = false;
                goTo(REVIEW);
                return;
            }
            goTo(current + 1);
        });
    }
    if (prevBtn) {
        prevBtn.addEventListener("click", () => goTo(current - 1));
    }
    document.querySelectorAll("[data-start]").forEach((btn) => {
        btn.addEventListener("click", () => {
            if (btn.hasAttribute("data-start-rozsz") && options.onRequestRozsz) {
                options.onRequestRozsz();
            }
            goTo(1);
        });
    });
    document.querySelectorAll("[data-tasklist-open]").forEach((btn) => {
        btn.addEventListener("click", openTaskList);
    });
    document.querySelectorAll("[data-tasklist-close]").forEach((btn) => {
        btn.addEventListener("click", () => goTo(current));
    });
    document.querySelectorAll("[data-task-step]").forEach((btn) => {
        btn.addEventListener("click", () => goTo(Number(btn.dataset.taskStep)));
    });

    // szyna: klikalne tylko kroki ukończone (proces zostaje liniowy)
    railItems.forEach((item) => {
        item.addEventListener("click", () => {
            const nr = Number(item.dataset.progressStep);
            if (item.dataset.state === "done" || nr < current) goTo(nr);
        });
    });

    // Enter = Dalej. W textarea Enter łamie wiersz (Ctrl/⌘+Enter przechodzi).
    form.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" || event.isComposing) return;
        const target = event.target;
        if (!target || target.tagName === "BUTTON" || target.type === "submit") return;
        if (target.tagName === "TEXTAREA" && !(event.metaKey || event.ctrlKey)) return;
        if (current === REVIEW) return; // tam Enter ma trafić w „Wyślij brief"
        event.preventDefault();
        if (nextBtn) nextBtn.click();
    });

    window.addEventListener("popstate", (event) => {
        const state = event.state || {};
        if (state.tasklist) {
            tasklistOpen = true;
            paintTaskList();
            render({});
            return;
        }
        const nr = typeof state.briefStep === "number" ? state.briefStep : nrFromHash(window.location.hash) || 0;
        tasklistOpen = false;
        current = Math.max(0, Math.min(REVIEW, nr));
        render({});
    });

    // wejście z deep-linkiem (#krok-5 z maila) — tylko do granicy kompletności
    const fromHash = nrFromHash(window.location.hash);
    if (fromHash !== null && fromHash > 0) {
        const limit = firstIncomplete();
        goTo(Math.min(fromHash, limit), { replace: true, silent: true });
    } else {
        goTo(0, { replace: true, silent: true });
    }

    return {
        goTo,
        openTaskList,
        paintTaskList,
        getCurrent: () => current,
        getTotal: () => total,
        getReviewNr: () => REVIEW,
        stepEl,
        // „Zmień" z podsumowania: wróć do kroku, a potem prosto na podsumowanie
        editFromReview(nr) {
            returnToReview = true;
            goTo(nr);
        },
        validateAll() {
            for (let nr = 1; nr <= total; nr++) {
                const el = stepEl(nr);
                if (el) {
                    el.hidden = false; // walidacja liczy tylko :not(disabled), hidden krok psuje pomiary
                    const ok = validateStep(el, form, { focus: false });
                    el.hidden = Number(el.dataset.step) !== current;
                    if (!ok) return nr;
                }
            }
            return 0;
        },
    };
}
